import { NgModule, ErrorHandler, Injector } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { ASSETS_PATH, HelpersModule } from '@anecdotes/helpers';
import { APP_CONFIG, AppConfigService} from '@anecdotes/configuration-data-access';
import { TranslateConfigModule } from '@anecdotes/translate-config';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@anecdotes/service-worker';
import { externalModules } from './external-modules';
import { AlertModule } from '@anecdotes/design-system/alert';
import { DataDogConfiguration, MonitoringErrorHandler, MonitoringModule } from '@anecdotes/monitoring';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { RealtimeDbModule } from '@anecdotes/realtime-db';
import { DS_LOG_CONFIG, LogConfig } from '@anecdotes/design-system/injection-tokens';
import { MaintenanceInterceptor, MaintenanceStateService, MobileAndNotSupportBrowserService } from './services';
import { MaintenanceModeComponent, MobileComingSoonComponent, PluginRedirectionCallbackComponent } from './components';
import { SvgIconsModule } from '@anecdotes/design-system/svg-icons';
import { LoadersModule } from '@anecdotes/design-system/loaders';
import { UtilsModule } from '@anecdotes/design-system/utils';
import { routesFactory } from './routes-factory.function';
import { configWrapper } from '../environments/config';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_BASE_HREF } from '@angular/common';

const ddConfig: DataDogConfiguration = configWrapper.config.dataDog ? {
  envName: configWrapper.config.envName,
  appVersion: configWrapper.config.releaseVersion,
  ...configWrapper.config.dataDog
} : null;

@NgModule({
  declarations: [
    AppComponent,
    MobileComingSoonComponent,
    PluginRedirectionCallbackComponent,
    MaintenanceModeComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.use({ isEnabled: configWrapper.config.serviceWorker, pollingInterval: 60 }),
    provideFirebaseApp(() => initializeApp(configWrapper.config.firebase)),
    provideAuth((injector) => getAuth(injector.get(FirebaseApp))),
    RealtimeDbModule,
    SvgIconsModule.forRoot(),
    MonitoringModule.withConfig(ddConfig),
    StoreModule.forRoot({},{
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    }),
    AlertModule.forRoot(),
    LoadersModule.forRoot(),
    EffectsModule.forRoot([]),
    externalModules,
    HelpersModule.forRoot(),
    TranslateConfigModule.forRoot(),
    RouterModule.forRoot(
      routesFactory(configWrapper.config, location.origin),
      { initialNavigation: 'enabledNonBlocking' }
    ),
    UtilsModule.forRoot((injector: Injector) => {
      const configService = injector.get(AppConfigService);

      return {
        anecdotesUrl: configService.config.redirectUrls.anecdotes,
      };
    })
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ASSETS_PATH,
      useValue: 'apps/assets'
    },
    {
      provide: DS_LOG_CONFIG,
      useValue: { useLogging: configWrapper.config.production } as LogConfig
    },
    {
      provide: ErrorHandler,
      useClass: MonitoringErrorHandler
    },
    {
      provide: APP_CONFIG,
      useValue: configWrapper.config
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true
    },
    {
      provide: APP_BASE_HREF,
      useValue: '/'
    },
    MobileAndNotSupportBrowserService,
    MaintenanceStateService,
  ]
})
export class AppModule {}
